/**
 * Push Notification Utilities
 * Client-side push notification management and service worker communication
 */

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface NotificationPermissionResult {
  granted: boolean;
  denied: boolean;
  default: boolean;
  supported: boolean;
}

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screen: {
    width: number;
    height: number;
  };
  timezone: string;
  online: boolean;
}

export class PushNotificationManager {
  private vapidPublicKey: string | null = null;
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
  private currentSubscription: PushSubscription | null = null;

  constructor() {
    this.initializeServiceWorker();
  }

  // =======================
  // INITIALIZATION
  // =======================

  private async initializeServiceWorker(): Promise<void> {
    try {
      if (!this.isSupported()) {
        console.warn('Push notifications are not supported in this browser');
        return;
      }

      // Wait for service worker registration
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        this.serviceWorkerRegistration = registration;
        
        // Get current subscription if exists
        this.currentSubscription = await registration.pushManager.getSubscription();
        
        console.log('📱 Push notification manager initialized');
      }
    } catch (error) {
      console.error('Failed to initialize push notification manager:', error);
    }
  }

  // =======================
  // SUPPORT DETECTION
  // =======================

  public isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  public async getPermissionStatus(): Promise<NotificationPermissionResult> {
    if (!this.isSupported()) {
      return {
        granted: false,
        denied: false,
        default: false,
        supported: false
      };
    }

    const permission = await Notification.requestPermission();
    
    return {
      granted: permission === 'granted',
      denied: permission === 'denied',
      default: permission === 'default',
      supported: true
    };
  }

  // =======================
  // PERMISSION MANAGEMENT
  // =======================

  public async requestPermission(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        throw new Error('Push notifications are not supported');
      }

      const permission = await Notification.requestPermission();
      console.log('📱 Notification permission:', permission);
      
      return permission === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  public async checkPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    return Notification.permission === 'granted';
  }

  // =======================
  // VAPID KEY MANAGEMENT
  // =======================

  public async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await fetch('/api/notifications/vapid-key');
      const data = await response.json();
      
      if (data.success && data.data.vapidPublicKey) {
        this.vapidPublicKey = data.data.vapidPublicKey;
        return this.vapidPublicKey;
      } else {
        throw new Error('Failed to get VAPID public key');
      }
    } catch (error) {
      console.error('Failed to fetch VAPID public key:', error);
      throw error;
    }
  }

  // =======================
  // SUBSCRIPTION MANAGEMENT
  // =======================

  public async subscribe(): Promise<PushSubscriptionData | null> {
    try {
      // Check permissions
      const hasPermission = await this.checkPermission();
      if (!hasPermission) {
        const granted = await this.requestPermission();
        if (!granted) {
          throw new Error('Push notification permission denied');
        }
      }

      // Get service worker registration
      if (!this.serviceWorkerRegistration) {
        await this.initializeServiceWorker();
        if (!this.serviceWorkerRegistration) {
          throw new Error('Service worker not available');
        }
      }

      // Get VAPID public key
      const vapidKey = await this.getVapidPublicKey();

      // Subscribe to push notifications
      const subscription = await this.serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
      });

      this.currentSubscription = subscription;

      // Convert to our format
      const subscriptionData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        }
      };

      // Send subscription to server
      await this.sendSubscriptionToServer(subscriptionData);

      console.log('📱 Successfully subscribed to push notifications');
      return subscriptionData;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  public async unsubscribe(): Promise<boolean> {
    try {
      if (!this.currentSubscription) {
        // Try to get current subscription
        if (this.serviceWorkerRegistration) {
          this.currentSubscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
        }
      }

      if (this.currentSubscription) {
        // Unsubscribe from push manager
        const success = await this.currentSubscription.unsubscribe();
        
        if (success) {
          // Notify server
          await this.sendUnsubscriptionToServer(this.currentSubscription.endpoint);
          this.currentSubscription = null;
          
          console.log('📱 Successfully unsubscribed from push notifications');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  public async isSubscribed(): Promise<boolean> {
    try {
      if (!this.serviceWorkerRegistration) {
        return false;
      }

      const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
      this.currentSubscription = subscription;
      
      return !!subscription;
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      return false;
    }
  }

  public async getCurrentSubscription(): Promise<PushSubscriptionData | null> {
    try {
      if (!this.serviceWorkerRegistration) {
        return null;
      }

      const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
      
      if (!subscription) {
        return null;
      }

      this.currentSubscription = subscription;

      return {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        }
      };
    } catch (error) {
      console.error('Failed to get current subscription:', error);
      return null;
    }
  }

  // =======================
  // SERVER COMMUNICATION
  // =======================

  private async sendSubscriptionToServer(subscription: PushSubscriptionData): Promise<void> {
    try {
      const deviceInfo = this.getDeviceInfo();
      
      const response = await fetch('/api/notifications/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subscription,
          deviceInfo
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to send subscription to server');
      }
    } catch (error) {
      console.error('Failed to send subscription to server:', error);
      throw error;
    }
  }

  private async sendUnsubscriptionToServer(endpoint: string): Promise<void> {
    try {
      const response = await fetch('/api/notifications/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ endpoint })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to send unsubscription to server');
      }
    } catch (error) {
      console.error('Failed to send unsubscription to server:', error);
      throw error;
    }
  }

  // =======================
  // TEST NOTIFICATIONS
  // =======================

  public async sendTestNotification(title?: string, message?: string): Promise<boolean> {
    try {
      const response = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title,
          message
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Failed to send test notification:', error);
      return false;
    }
  }

  // =======================
  // SERVICE WORKER COMMUNICATION
  // =======================

  public setupServiceWorkerMessageListener(): void {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, data } = event.data;

      switch (type) {
        case 'NOTIFICATION_NAVIGATE':
          // Handle navigation from notification click
          if (data.url && window.location.pathname !== data.url) {
            window.history.pushState({}, '', data.url);
            // Trigger navigation event for router
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
          break;

        case 'BADGE_COUNT_UPDATE':
          // Handle badge count update
          this.updateBadgeCount(data.count);
          break;

        default:
          console.log('Unknown service worker message:', event.data);
      }
    });
  }

  private updateBadgeCount(count: number): void {
    // Update badge in title
    const baseTitle = 'Custom Design Manager';
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;

    // Update favicon if needed
    this.updateFaviconBadge(count);

    // Dispatch event for components to listen to
    window.dispatchEvent(new CustomEvent('notification-badge-update', {
      detail: { count }
    }));
  }

  private updateFaviconBadge(count: number): void {
    // Create or update favicon with badge
    // This is a basic implementation - could be enhanced with canvas rendering
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon && count > 0) {
      // Add badge indication to favicon (simplified)
      favicon.href = '/icons/icon-badge.ico';
    } else if (favicon) {
      favicon.href = '/icons/icon.ico';
    }
  }

  // =======================
  // UTILITY METHODS
  // =======================

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    
    return window.btoa(binary);
  }

  private getDeviceInfo(): DeviceInfo {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screen: {
        width: screen.width,
        height: screen.height
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine
    };
  }
}

// Singleton instance
let pushNotificationManagerInstance: PushNotificationManager | null = null;

export const getPushNotificationManager = (): PushNotificationManager => {
  if (!pushNotificationManagerInstance) {
    pushNotificationManagerInstance = new PushNotificationManager();
  }
  return pushNotificationManagerInstance;
};

export default PushNotificationManager;